import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { fetchServiceDetail } from '../api';
import '../styles/pages/SingleService.scss';

/**
 * Determines if a URL points to a video file.
 */
function isVideo(url) {
    if (!url) return false;
    const ext = url.split('?')[0].split('.').pop().toLowerCase();
    return ['mp4', 'webm', 'ogg', 'mov'].includes(ext);
}

const SingleService = () => {
    const { slug } = useParams();
    const [service, setService] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    usePageTitle(service ? service.title : 'Services');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        fetchServiceDetail(slug)
            .then((data) => {
                if (!cancelled) {
                    setService(data);
                    setLoading(false);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err.message);
                    setLoading(false);
                }
            });

        return () => { cancelled = true; };
    }, [slug]);

    if (loading) {
        return (
            <>
                <Navbar />
                <div className="page-single-service">
                    <div className="container text-center py-5">
                        <p className="text-muted">Loading...</p>
                    </div>
                </div>
            </>
        );
    }

    if (error || !service) {
        return (
            <>
                <Navbar />
                <div className="page-single-service">
                    <div className="container text-center py-5">
                        <p className="text-danger">{error || 'Service not found.'}</p>
                        <Link to="/offerings" className="btn btn-dark mt-3">Back to Services</Link>
                    </div>
                </div>
            </>
        );
    }

    // Hero media (wide hero image or first gallery item)
    const heroMedia = service.hero_image_url || service.image_url;

    return (
        <>
            <Navbar />
            <div className="page-single-service">
                {/* Hero Section */}
                <section className="single-service-hero text-center">
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-lg-8">
                                <h1>{service.title}</h1>
                                {service.intro && <p className="lead">{service.intro}</p>}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Hero Image */}
                {heroMedia && (
                    <section className="single-service-image">
                        <img className="w-100" src={heroMedia} alt={service.title} />
                    </section>
                )}

                {/* Details Section */}
                {service.details && service.details.length > 0 && (
                    <section className="single-service-content">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-12 col-lg-10">
                                    {service.section_heading && (
                                        <div className="row justify-content-between">
                                            <div className="col-12 col-lg-10 col-xl-8">
                                                <h2>{service.section_heading}</h2>
                                            </div>
                                        </div>
                                    )}
                                    <div className="row service-items">
                                        {service.details.map((item, index) => (
                                            <div className="col-12 col-lg-6" key={index}>
                                                <h4>{item.title}</h4>
                                                <p>{item.description}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Features List */}
                {service.features && service.features.length > 0 && (
                    <section className="single-service-features">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-12 col-lg-10">
                                    <h3>What's Included</h3>
                                    <ul className="features-list">
                                        {service.features.map((feature, i) => (
                                            <li key={i}>{feature}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Pricing */}
                {service.pricing && (
                    <section className="single-service-pricing">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-12 col-lg-10 text-center">
                                    <div className="pricing-card">
                                        <span className="pricing-amount">
                                            {service.pricing.currency} {service.pricing.amount.toLocaleString()}
                                        </span>
                                        {service.pricing.per && (
                                            <span className="pricing-per">/ {service.pricing.per}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Media Gallery */}
                {service.images && service.images.length > 0 && (
                    <section className="single-service-gallery">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-12 col-lg-10">
                                    <h3>Gallery</h3>
                                    <div className="gallery-grid">
                                        {service.images.map((url, i) => (
                                            <div className="gallery-item" key={i}>
                                                {isVideo(url) ? (
                                                    <video
                                                        src={url}
                                                        controls
                                                        playsInline
                                                        preload="metadata"
                                                        className="gallery-media"
                                                    />
                                                ) : (
                                                    <img src={url} alt={`${service.title} ${i + 1}`} className="gallery-media" />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Navigation */}
                <section className="single-service-nav">
                    <div className="container">
                        <div className="page-navigation">
                            <Link to="/offerings" className="btn btn-light btn-circle">all services</Link>
                        </div>
                    </div>
                </section>
            </div>
            <Footer />
        </>
    );
};

export default SingleService;
